import { autocompletion, Completion, CompletionContext } from '@codemirror/autocomplete'
import { PostgreSQL, sql } from '@codemirror/lang-sql'
import { InfoRounded } from '@mui/icons-material'
import { Stack, Tooltip, Typography } from '@mui/material'
import CodeMirror from '@uiw/react-codemirror'
import _ from 'lodash'
import ApiClient from '../../../backend/ApiClient'
import algo from '../../../core/Algo'
import { IFlow, INode, NodeType } from '../../../definitions/Nodes'
import { IAcceptedData } from '../../../definitions/NodesDescription'
import NodeManager from '../../../managers/NodeManager'
import { useAppSelector } from '../../../store/Hooks'
import useQuery, { buildQueryKey } from '../../hooks/useQuery'

interface P {
    acceptedData: IAcceptedData<NodeType>
    selectedNode: INode<NodeType>
    flow: IFlow
    setSelected: (node: INode) => void
    disable: boolean
}

const renderTooltip = (title: string, disable: boolean) => (
    <Tooltip arrow title={title} disableHoverListener={disable}>
        <InfoRounded sx={{ cursor: disable ? undefined : 'pointer', color: disable ? 'var(--icon-disabled)' : 'var(--icon-color)' }} />
    </Tooltip>
)

function NodeSQLEditor({ selectedNode, acceptedData, flow, setSelected, disable }: P) {
    const { response: dbMetadata, status: dbMetadataStatus } = useQuery<any>({
        queryKey: buildQueryKey('dbMetadata'),
        queryFn: () => ApiClient.Flow.getDbMetadata(),
        cache: true
    })

    const allMetadata = dbMetadataStatus === 'success' ? dbMetadata : []

    const value: string = selectedNode.data[acceptedData.parameter]
    const errorNoInput = acceptedData.isRequired && (!algo.hasVal(value) || value.length === 0)
    const mode = useAppSelector(state => state.app.mode)

    const listKeyValue = NodeManager.getListInputs(flow, selectedNode) ?? []

    const listMentions: Completion[] = listKeyValue.map(x => {
        return {
            label: x.label,
            apply: '@{' + x.label + '}'
        }
    })

    let trimedText: string = _.cloneDeep(value) ?? ''
    for (let i = 0; i < listKeyValue.length; i++) {
        const keyValue = listKeyValue[i]
        trimedText = trimedText.replaceAll(keyValue.value, keyValue.label)
    }

    const myCompletions = (context: CompletionContext) => {
        const word = context.matchBefore(/\w*/)
        if (word.from === word.to && !context.explicit) return null
        return {
            from: word.from,
            options: [...listMentions, ...allMetadata]
        }
    }

    return (
        <Stack spacing={2}>
            <Stack flexDirection='row' gap={1}>
                {renderTooltip(acceptedData?.tooltipContent, disable)}
                <Typography variant='subtitle2'>{acceptedData?.header ?? null}</Typography>
            </Stack>
            <CodeMirror
                readOnly={disable}
                theme={mode === 'dark' ? 'dark' : 'light'}
                style={{ color: 'red', colorScheme: 'dark', border: errorNoInput ? '1px solid red' : undefined, flex: 1, height: '100%', display: 'flex', overflow: 'auto' }}
                value={trimedText}
                width='100%'
                minHeight='30vh'
                extensions={[sql({ dialect: PostgreSQL }), autocompletion({ override: [myCompletions] })]}
                onChange={(value: string) => {
                    let trimmedValue: string = _.cloneDeep(value) ?? ''
                    for (let i = 0; i < listKeyValue.length; i++) {
                        const keyValue = listKeyValue[i]
                        trimmedValue = trimmedValue.replaceAll(keyValue.label, keyValue.value)
                    }
                    setSelected({ ...selectedNode, data: { ...selectedNode.data, [acceptedData.parameter]: trimmedValue } })
                }}
            />
        </Stack>
    )
}

export default NodeSQLEditor
